import { useNavigationManager } from '@Router/useNavigationManager';
import { z } from 'zod';
import { useFormHook, useSetError } from '@Hooks/index';
import { OnboardingFormFields } from '@Types/index';
import { phoneFormScheme } from '@Pages/happyFlowPages/phone/phoneForm/phoneFormScheme';
import { useFormState } from 'react-hook-form';
import { useBoundStore } from '@Store/useBoundStore';
import { AdminPersonalDetailsFormType } from '../AdminPersonalDetailsForm';
import { birthDateSchema } from '@Schema/birthDateSchema';
import { useStartAuthorizedUserOnboarding } from '@Pages/inviteTeamMember/AccountantPersonalDetails/api/authorizedUser.api';
import { useCreateFullPageSnapShot } from '@Services/snapShot/useCreateFullPageSnapShot';
import { useState } from 'react';

const adminPersonalDetailsSchema = z.object({
  ...birthDateSchema.shape,
  ...phoneFormScheme.shape,
});

export const useAdminPersonalDetailsLogic = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { navigateToNextPage } = useNavigationManager();
  const { getCustomerData, setCustomerData } = useBoundStore((state) => state);
  const { mutateAsync: saveDetails } = useStartAuthorizedUserOnboarding();
  const { setConsentFormData } = useBoundStore((state) => state);
  const { takeScreenShot } = useCreateFullPageSnapShot();

  const defaultValues: AdminPersonalDetailsFormType = {
    [OnboardingFormFields.DATE_OF_BIRTH]: '',
    [OnboardingFormFields.PHONE_NUMBER]: '',
  };

  const formMethods = useFormHook({
    formSchema: adminPersonalDetailsSchema,
    formDefaultValues: defaultValues,
    schemaTitle: 'invite-admin-personal-details-form',
  });

  const { isValid } = useFormState({ control: formMethods.control });

  const onSubmit = async (data: AdminPersonalDetailsFormType) => {
    try {
      setIsLoading(true);
      await saveDetails({
        dateOfBirth: data[OnboardingFormFields.DATE_OF_BIRTH],
        phoneNumber: data[OnboardingFormFields.PHONE_NUMBER],
      });
      const consentFormData = await takeScreenShot({
        nodes: [
          document.querySelector('#page-content') as HTMLElement,
          document.querySelector('#page-footer') as HTMLElement,
        ],
      });
      if (consentFormData) {
        setConsentFormData(consentFormData);
      }
      const customerDataToUpdate = getCustomerData();
      customerDataToUpdate.customerData.birthDate = data[OnboardingFormFields.DATE_OF_BIRTH];
      customerDataToUpdate.customerData.phoneNumber = data[OnboardingFormFields.PHONE_NUMBER];
      setCustomerData(customerDataToUpdate);
      setIsLoading(false);
      navigateToNextPage();
    } catch {
      setIsLoading(false);
    }
  };

  useSetError({ formMethods });

  return {
    formMethods,
    isValid,
    handleSubmit: formMethods.handleSubmit(onSubmit),
    isLoading,
  };
};
